import React, { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import CustomInput from '../../Components/CommonComponents/CustomInput';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getJwtToken, END_POINT } from '../../Redux/AdminReducer/action';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import axios from 'axios';
import CustomSelect from '../../Components/CommonComponents/CustomSelect';
import { downloadOrderPdf } from './orderPdf';
import { readCart, removeFromCart } from './medicineCart';
import MedicineNameField from './MedicineNameField';

const fetchSingleItem = async (id) => {
  try {
    const result = await axios.get(`${END_POINT}/inventory/orders/${id}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.log(error);

    toast.error('Something went wrong while fetching data');
  }
};

// Ids go in the body: a long list in the query string overflows the server's
// request size limit.
const fetchMedicine = async (ids) => {
  if (!ids?.length) return { data: [] };
  try {
    const result = await axios.post(
      `${END_POINT}/inventory/inventoryIds`,
      { ids },
      {
        headers: {
          Authorization: getJwtToken(),
        },
      }
    );
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.error(error);
    toast.error('Something went wrong while fetching data');
  }
};

const fetchLowStock = async () => {
  try {
    return await axios.get(`${END_POINT}/inventory/inventory/lowstock`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
  } catch (error) {
    console.error(error);
    toast.error('Could not load low stock medicines');
  }
};

// Where an order's medicines come from: ids in the path (single "Order"
// button), the persisted cart, or the server's current low-stock list.
const loadOrderMedicines = (medicineIds, source) => {
  if (medicineIds) return fetchMedicine(medicineIds.split(','));
  if (source === 'cart') return fetchMedicine(readCart());
  if (source === 'lowstock') return fetchLowStock();
  return Promise.resolve({ data: [] });
};

const fetchAllDistributors = async () => {
  try {
    const result = await axios.get(`${END_POINT}/inventory/distributors`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    console.error(error);
  }
};

const fetchDistributor = async (cname) => {
  // One entry per company, not per medicine: a low-stock order can hold
  // thousands of rows but only a handful of companies, and repeating them
  // overflows the URL. Medicines with no company would send "undefined".
  const companies = [...new Set((cname || []).map((c) => (c ?? '').trim()).filter(Boolean))];
  const params = companies.map((id) => `mname=${encodeURIComponent(id)}`).join('&');

  try {
    const result = await axios.get(`${END_POINT}/inventory/distributorsByMedicine?${params}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.error(error);
    toast.error('Something went wrong while fetching data');
  }
};

// The rate each row's distributor last charged for its medicine, in one
// request: a low-stock order can open with thousands of rows.
const fetchLastRates = async (rows) => {
  try {
    const items = rows.map(({ nameOfMedicine, potencyOrPower, distributor }) => ({
      medicine: nameOfMedicine,
      distributor,
      potency: potencyOrPower || undefined,
    }));
    const result = await axios.post(
      `${END_POINT}/inventory/orders/last-rates`,
      { items },
      {
        headers: {
          Authorization: getJwtToken(),
        },
      }
    );
    return Array.isArray(result?.data) ? result.data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const rateKey = (row) =>
  row?.nameOfMedicine && row?.distributor
    ? [row.nameOfMedicine, row.potencyOrPower || '', row.distributor].map((v) => String(v).trim().toLowerCase()).join('|')
    : '';

const formatShortDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Order-level invoice details. An order is a group of medicine lines on the
// server, which copies these onto every line of the order.
const ORDER_FIELDS = ['invoiceNo', 'invoiceDate', 'dispatchDate', 'receiptDate', 'notes'];
const initialOrderDetails = { invoiceNo: '', invoiceDate: '', dispatchDate: '', receiptDate: '', notes: '' };

const isObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(String(value || ''));

// yyyy-mm-dd for <input type="date">.
const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const isBlankValue = (value) => value === '' || value === null || value === undefined;

// Same maths as the server: qty x rate, less discount %, plus GST % on the rest.
const lineAmounts = (row) => {
  if (isBlankValue(row?.rate) || !Number.isFinite(Number(row.rate))) return null;
  const gross = (Number(row.quantity) || 0) * Number(row.rate);
  const discountAmount = (gross * (Number(row.discount) || 0)) / 100;
  const taxable = gross - discountAmount;
  const gstAmount = (taxable * (Number(row.gst) || 0)) / 100;
  return { gross, discountAmount, taxable, gstAmount, total: taxable + gstAmount };
};

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// A saved line as the form edits it: dates as yyyy-mm-dd and the distributor
// by company name, which is what the distributor select holds.
const toFormRow = (line) => ({
  ...line,
  expiryDate: toDateInput(line?.expiryDate),
  distributor: line?.distributorName || line?.distributor || '',
  rate: line?.rate ?? '',
  discount: line?.discount ?? '',
  gst: line?.gst ?? '',
  batchNo: line?.batchNo || '',
  hsnNo: line?.hsnNo || '',
});

const toOrderDetails = (source) => ({
  invoiceNo: source?.invoiceNo || '',
  invoiceDate: toDateInput(source?.invoiceDate),
  dispatchDate: toDateInput(source?.dispatchDate),
  receiptDate: toDateInput(source?.receiptDate),
  notes: source?.notes || '',
});

const fetchOrderByOrderId = async (orderId) => {
  try {
    return await axios.get(`${END_POINT}/inventory/orders/by-order-id/${encodeURIComponent(orderId)}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
  } catch (error) {
    console.error(error);
    toast.error('Something went wrong while fetching the order');
  }
};

// One submission = one order = one Order_Id, whatever the number of medicines.
// The order is no longer emailed; the PDF that downloads here is the order copy.
const createItem = async (items, orderedInventoryIds, navigate, setIsPorcessing, orderDetails = {}) => {
  try {
    // Still posted as an array (the shape every server version accepts); the
    // server takes the order-level fields off the first line and applies them
    // to the whole order.
    const payload = items.map((item, index) => (index === 0 ? { ...item, ...orderDetails } : item));
    const result = await axios.post(`${END_POINT}/inventory/orders`, payload, {
      headers: {
        Authorization: getJwtToken(),
      },
    });

    const { Order_Id, items: createdItems } = result?.data || {};
    toast.success(`Order ${Order_Id || ''} created`);
    // Only now does the cart give these medicines up.
    removeFromCart(orderedInventoryIds);

    try {
      await downloadOrderPdf({
        Order_Id,
        date: createdItems?.[0]?.date || new Date(),
        status: 'pending',
        items: createdItems || [],
      });
    } catch (pdfError) {
      console.log('Order PDF failed', pdfError);
      toast.error('Order saved, but the PDF could not be generated. Download it from the Orders list.');
    }

    setIsPorcessing(false);
    setTimeout(() => {
      navigate(`/main/inventory/Order`);
    }, 1200);
  } catch (error) {
    toast.error(error?.response?.data?.error || error?.response?.data || 'Something went wrong');
    setIsPorcessing(false);
  }
};

const updateItem = async (data, setIsPorcessing) => {
  try {
    const result = await axios.put(`${END_POINT}/inventory/orders/${data._id}`, data, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    toast.success('Details Updated');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    setIsPorcessing(false);
    toast.error('Failed To Updated');
  }
};

// Save a whole order (opened by its Order_Id): invoice details plus every line.
// Lines removed in the form are removed from the order.
const updateOrder = async (orderId, items, orderDetails, navigate, setIsPorcessing) => {
  try {
    await axios.put(
      `${END_POINT}/inventory/orders/by-order-id/${encodeURIComponent(orderId)}`,
      { ...orderDetails, items },
      {
        headers: {
          Authorization: getJwtToken(),
        },
      }
    );
    toast.success(`Order ${orderId} updated`);
    setIsPorcessing(false);
    setTimeout(() => {
      navigate(`/main/inventory/Order`);
    }, 1000);
  } catch (error) {
    toast.error(error?.response?.data?.error || 'Failed to update the order');
    setIsPorcessing(false);
  }
};

const initialFormData = {
  nameOfMedicine: '',
  company: '',
  potencyOrPower: '',
  quantity: '',
  rate: '',
  discount: '',
  gst: '',
  batchNo: '',
  hsnNo: '',
  typeOfMedicine: '',
  packing: '',
  expiryDate: '',
  damage: '',
  date: '',
  distributor: '',
};

const initialFormError = initialFormData;

// How much to reorder when the order was raised off the minimum threshold:
// top the medicine back up to its maximum level, or to twice its minimum when
// no maximum is recorded.
const suggestedQuantity = (item) => {
  const current = Number(item?.quantity) || 0;
  const min = Number(item?.minQuantity) || 0;
  const max = Number(item?.maxQuantity) || 0;
  const target = max > 0 ? max : min * 2;
  return Math.max(target - current, 1);
};

export default function OrderForm() {
  // define form state
  const [formData, setFormData] = useState([initialFormData]);
  const [orderDetails, setOrderDetails] = useState(initialOrderDetails);
  const [formError, setFormError] = useState(initialFormError);
  const navigate = useNavigate();
  const { inventory_item_id, medicine_id } = useParams();
  const [searchParams] = useSearchParams();
  const isThresholdOrder = searchParams.get('auto') === '1';
  const orderSource = searchParams.get('source');
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isProcessing, setIsPorcessing] = useState(false);
  let [distributorsOptions, setDistributorsOptions] = useState([]);
  // Last known rate per medicine + distributor, keyed by rateKey().
  const [lastRates, setLastRates] = useState({});
  const requestedRates = useRef(new Set());

  useEffect(() => {
    if (inventory_item_id === 'addNew') {
      if (medicine_id || orderSource) {
        loadOrderMedicines(medicine_id, orderSource).then((e) => {
          setSelectedMedicine(e?.data);

          const mappedFormData = e?.data?.map((item) => {
            const { _id, company, nameOfMedicine, potencyOrPower, typeOfMedicine, distributorName } = item;
            return {
              inventoryId: _id,
              company,
              nameOfMedicine,
              potencyOrPower,
              typeOfMedicine,
              quantity: suggestedQuantity(item) || '',
              distributor: distributorName || '',
            };
          });
          if (mappedFormData && mappedFormData.length > 0) {
            setFormData(mappedFormData);
          } else {
            setFormData([initialFormData]);
          }
        });
      } else {
        setFormData([initialFormData]);
      }
    } else if (inventory_item_id && !isObjectId(inventory_item_id)) {
      // Opened by Order_Id: edit the whole order.
      fetchOrderByOrderId(inventory_item_id).then((e) => {
        if (!e?.data) return;
        // Every line carries the order-level fields, so the first line is a fallback.
        setOrderDetails(toOrderDetails('invoiceNo' in e.data ? e.data : e.data.items?.[0]));
        const lines = (e.data.items || []).map(toFormRow);
        setFormData(lines.length > 0 ? lines : [initialFormData]);
      });
    } else if (inventory_item_id) {
      fetchSingleItem(inventory_item_id).then((e) => {
        if (!e?.data) return;
        const lines = Array.isArray(e.data) ? e.data : [e.data];
        setOrderDetails(toOrderDetails(lines[0]));
        setFormData(lines.map(toFormRow));
      });
    }
  }, [inventory_item_id, medicine_id, isThresholdOrder, orderSource]);

  useEffect(() => {
    if (selectedMedicine && selectedMedicine.length > 0) {
      fetchDistributor(selectedMedicine?.map((e) => e?.company)).then((e) => {
        setDistributorsOptions(e?.data || []);
      });
    } else {
      fetchAllDistributors().then((e) => {
        setDistributorsOptions(e?.data || []);
      });
    }
  }, [selectedMedicine]);

  // Pre-fill the rate from the last purchase from that distributor. A rate the
  // user typed is never overwritten; an auto-filled one follows the row's
  // medicine/distributor when those change.
  useEffect(() => {
    const pending = [];
    formData.forEach((row) => {
      const key = rateKey(row);
      if (!key || requestedRates.current.has(key)) return;
      requestedRates.current.add(key);
      pending.push({ key, row });
    });
    if (pending.length === 0) return;
    fetchLastRates(pending.map((p) => p.row)).then((results) => {
      setLastRates((prev) => {
        const next = { ...prev };
        pending.forEach(({ key }, index) => {
          const last = results[index];
          next[key] = last?.rate != null ? last : null;
        });
        return next;
      });
    });
  }, [formData]);

  useEffect(() => {
    // Editing an existing line keeps whatever rate it was saved with.
    if (inventory_item_id !== 'addNew') return;
    setFormData((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const last = lastRates[rateKey(row)];
        const isEmpty = row?.rate === '' || row?.rate === undefined || row?.rate === null;
        if (last?.rate != null && isEmpty && !row?.rateEdited) {
          changed = true;
          return { ...row, rate: last.rate, rateAuto: true };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [lastRates, formData, inventory_item_id]);

  // handel input change
  // Nothing is emailed any more, so the distributor is just a name on the order;
  // the server resolves it to a distributor record when it saves.
  const handleInputChange = (event, rowIndex) => {
    const { name, value } = event.target;
    const updatedFormData = [...formData];

    if (!updatedFormData[rowIndex]) {
      updatedFormData[rowIndex] = { ...initialFormData };
    }

    updatedFormData[rowIndex] = { ...updatedFormData[rowIndex], [name]: value };

    if (name === 'rate') {
      updatedFormData[rowIndex].rateAuto = false;
      updatedFormData[rowIndex].rateEdited = true;
    } else if (['nameOfMedicine', 'potencyOrPower', 'distributor'].includes(name) && updatedFormData[rowIndex].rateAuto) {
      updatedFormData[rowIndex].rate = '';
      updatedFormData[rowIndex].rateAuto = false;
    }

    setFormData(updatedFormData);
  };

  // A medicine picked from the inventory fills in the rest of its row. Expiry
  // stays empty (it belongs to the batch that arrives) and a quantity already
  // typed is kept.
  const handleMedicinePick = (item, rowIndex) => {
    setFormData((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;
        return {
          ...row,
          inventoryId: item?._id,
          nameOfMedicine: item?.nameOfMedicine || '',
          company: item?.company || '',
          potencyOrPower: item?.potencyOrPower || '',
          typeOfMedicine: item?.typeOfMedicine || '',
          packing: item?.packagingSize || row.packing || '',
          distributor: item?.distributorName || row.distributor || '',
          quantity: row.quantity || suggestedQuantity(item),
          rate: row.rateEdited ? row.rate : '',
          rateAuto: false,
        };
      })
    );
  };

  // Typing a name that is not a stocked medicine unlinks the row from any
  // medicine picked before.
  const handleMedicineTyped = (text, rowIndex) => {
    handleInputChange({ target: { name: 'nameOfMedicine', value: text } }, rowIndex);
    setFormData((prev) => prev.map((row, index) => (index === rowIndex ? { ...row, inventoryId: undefined } : row)));
  };

  const handleForm = () => {
    // The whole array goes up as one request so the server stamps a single
    // Order_Id across every line.
    const orderedInventoryIds = formData.map((row) => row?.inventoryId).filter(Boolean);
    const trimmedFormData = formData.map(({ rateAuto, rateEdited, inventoryId, ...row }) => {
      const trimmedRow = { ...row };
      // Order-level fields go once, from the invoice details section.
      ORDER_FIELDS.forEach((key) => delete trimmedRow[key]);
      for (const key in trimmedRow) {
        if (typeof trimmedRow[key] === 'string') {
          trimmedRow[key] = trimmedRow[key].trim();
        }
      }
      return trimmedRow;
    });
    const trimmedOrderDetails = Object.fromEntries(
      ORDER_FIELDS.map((key) => [key, String(orderDetails[key] ?? '').trim()])
    );

    if (trimmedFormData.length === 0) {
      toast.error('Add at least one medicine');
      return false;
    }

    if (trimmedFormData.some((row) => !row.nameOfMedicine)) {
      toast.error('Every row needs a medicine name');
      return false;
    }

    if (trimmedFormData.some((row) => !(Number(row.quantity) > 0))) {
      toast.error('Quantity must be greater than 0 for every medicine');
      return false;
    }

    if (trimmedFormData.some((row) => row.rate !== '' && row.rate !== undefined && row.rate !== null && !(Number(row.rate) >= 0))) {
      toast.error('Rate must be a valid amount');
      return false;
    }

    if (trimmedFormData.some((row) => !row.distributor)) {
      toast.error('Please select distributor');
      return false;
    }

    const badPercent = (value) => !isBlankValue(value) && !(Number(value) >= 0 && Number(value) <= 100);
    if (trimmedFormData.some((row) => badPercent(row.discount))) {
      toast.error('Discount % must be between 0 and 100');
      return false;
    }
    if (trimmedFormData.some((row) => badPercent(row.gst))) {
      toast.error('GST % must be between 0 and 100');
      return false;
    }
    if (trimmedFormData.some((row) => row.hsnNo && !/^\d{4,8}$/.test(row.hsnNo))) {
      toast.error('HSN No. must be 4 to 8 digits');
      return false;
    }
    if (trimmedFormData.some((row) => (row.batchNo || '').length > 50)) {
      toast.error('Batch No. cannot be longer than 50 characters');
      return false;
    }
    if (trimmedOrderDetails.invoiceNo.length > 50) {
      toast.error('Invoice No. cannot be longer than 50 characters');
      return false;
    }
    if (trimmedOrderDetails.notes.length > 1000) {
      toast.error('Notes cannot be longer than 1000 characters');
      return false;
    }

    setFormError(initialFormError);
    setIsPorcessing(true);

    if (inventory_item_id === 'addNew') {
      createItem(trimmedFormData, orderedInventoryIds, navigate, setIsPorcessing, trimmedOrderDetails);
    } else if (!isObjectId(inventory_item_id)) {
      updateOrder(inventory_item_id, trimmedFormData, trimmedOrderDetails, navigate, setIsPorcessing);
    } else {
      // The single-line update stores `distributor` as a record id, so a
      // company name picked in the form goes in as the distributor name.
      const line = { ...trimmedFormData[0], ...trimmedOrderDetails };
      if (line.distributor && !isObjectId(line.distributor)) {
        line.distributorName = line.distributor;
        delete line.distributor;
      }
      updateItem(line, setIsPorcessing);
    }
    return true;
  };

  const isNew = inventory_item_id === 'addNew';
  // Rows that came from the inventory keep their medicine details locked; rows
  // added by hand below them stay fully editable.
  const lockedRows = selectedMedicine?.length || 0;
  const rows = formData;

  const removeRow = (index) => setFormData((prev) => prev.filter((_, i) => i !== index));

  const handleOrderDetailChange = (event) => {
    const { name, value } = event.target;
    setOrderDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Totals over the priced lines; a line without a rate has no amount.
  const totals = (rows || []).reduce(
    (sum, row) => {
      const amounts = lineAmounts(row);
      if (!amounts) return sum;
      return {
        subtotal: sum.subtotal + amounts.gross,
        discount: sum.discount + amounts.discountAmount,
        gst: sum.gst + amounts.gstAmount,
        net: sum.net + amounts.total,
        priced: sum.priced + 1,
      };
    },
    { subtotal: 0, discount: 0, gst: 0, net: 0, priced: 0 }
  );

  const smallInputClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition';

  return (
    <div className="min-h-screen w-full bg-slate-50 px-4 py-6 md:px-8">
      <div className="mx-auto w-full max-w-7xl">
        {/* Page header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
            >
              <FiArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">
                {isNew ? 'New Order' : `Edit Order${inventory_item_id && !isObjectId(inventory_item_id) ? ` ${inventory_item_id}` : ''}`}
              </h1>
              <p className="text-xs text-slate-500">
                {isThresholdOrder
                  ? 'These medicines are at or below their minimum threshold. Review the suggested quantities before submitting.'
                  : lockedRows > 0
                  ? 'Set a quantity and a distributor for each medicine, then submit to raise the order.'
                  : 'Add the medicines you want to order and pick a distributor for each.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isThresholdOrder && (
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Below threshold</span>
            )}
            {rows?.length > 0 && (
              <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                {rows.length} item{rows.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>

        {formData ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleForm();
            }}
            className="space-y-6"
          >
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-4">
                <h2 className="text-sm font-semibold tracking-tight text-slate-900">Invoice Details</h2>
                <p className="mt-0.5 text-xs text-slate-500">Optional. Fill these in from the distributor's invoice, now or once the goods arrive.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 px-6 py-4 sm:grid-cols-2 lg:grid-cols-4">
                <CustomInput label="Invoice No." name="invoiceNo" type="text" placeholder="Invoice number" value={orderDetails.invoiceNo} onChange={handleOrderDetailChange} />
                <CustomInput label="Invoice Date" name="invoiceDate" type="date" value={orderDetails.invoiceDate} onChange={handleOrderDetailChange} />
                <CustomInput label="Date Order Dispatched" name="dispatchDate" type="date" value={orderDetails.dispatchDate} onChange={handleOrderDetailChange} />
                <CustomInput label="Date of Receipt" name="receiptDate" type="date" value={orderDetails.receiptDate} onChange={handleOrderDetailChange} />
                <div className="sm:col-span-2 lg:col-span-4">
                  <label htmlFor="order-notes" className="mb-1 block text-xs font-medium text-gray-700 md:text-sm">
                    Notes
                  </label>
                  <textarea
                    id="order-notes"
                    name="notes"
                    rows={2}
                    maxLength={1000}
                    placeholder="Anything to remember about this order"
                    value={orderDetails.notes}
                    onChange={handleOrderDetailChange}
                    className={smallInputClass}
                  />
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-4">
                <h2 className="text-sm font-semibold tracking-tight text-slate-900">Order Information</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  All the medicines below are saved under a single order id, and the order PDF downloads automatically once you submit.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1900px] text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Name of Medicine</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Company</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Potency / Power</th>
                      <th className="w-28 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</th>
                      <th className="w-36 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Rate / Unit</th>
                      <th className="w-24 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Disc %</th>
                      <th className="w-24 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">GST %</th>
                      <th className="w-28 px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Type</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Packing</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Batch No.</th>
                      <th className="w-32 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">HSN No.</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Distributor</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Expiry Date</th>
                      <th className="w-14 px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows?.map((item, index) => {
                      const isLocked = index < lockedRows;
                      return (
                        <tr key={index} className="align-top transition hover:bg-slate-50/70">
                          <td className="px-3 py-2">
                            <MedicineNameField
                              disabled={isLocked}
                              value={item?.nameOfMedicine}
                              onPick={(medicine) => handleMedicinePick(medicine, index)}
                              onType={(text) => handleMedicineTyped(text, index)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="company" type="text" placeholder="Company" value={item?.company || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="potencyOrPower" type="text" placeholder="Potency" value={item?.potencyOrPower || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput name="quantity" type="number" placeholder="0" value={item?.quantity ?? ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            {/* Plain input: CustomInput has no step, and the default step of 1 would reject paise. */}
                            <div className="mt-1 w-full">
                              <input
                                name="rate"
                                type="number"
                                step="any"
                                min="0"
                                placeholder="0.00"
                                value={item?.rate ?? ''}
                                onChange={(e) => handleInputChange(e, index)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
                              />
                            </div>
                            {(() => {
                              const last = lastRates[rateKey(item)];
                              if (!last) return null;
                              const current = Number(item?.rate);
                              const diff = item?.rate !== '' && Number.isFinite(current) ? current - Number(last.rate) : 0;
                              return (
                                <p
                                  className={`mt-1 text-[11px] ${diff > 0 ? 'text-rose-600' : diff < 0 ? 'text-emerald-600' : 'text-slate-500'}`}
                                  title={last.Order_Id ? `Order ${last.Order_Id}` : undefined}
                                >
                                  Last: {last.rate}
                                  {last.date ? ` on ${formatShortDate(last.date)}` : ''}
                                  {diff > 0 ? ' (higher)' : diff < 0 ? ' (lower)' : ''}
                                </p>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2">
                            <div className="mt-1 w-full">
                              <input name="discount" type="number" step="any" min="0" max="100" placeholder="0" value={item?.discount ?? ''} onChange={(e) => handleInputChange(e, index)} className={smallInputClass} />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="mt-1 w-full">
                              <input name="gst" type="number" step="any" min="0" max="100" placeholder="0" value={item?.gst ?? ''} onChange={(e) => handleInputChange(e, index)} className={smallInputClass} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const amounts = lineAmounts(item);
                              if (!amounts) return <p className="mt-3 text-xs text-slate-300">—</p>;
                              return (
                                <div className="mt-2">
                                  <p className="font-medium text-slate-900">{formatMoney(amounts.total)}</p>
                                  {(amounts.discountAmount > 0 || amounts.gstAmount > 0) && (
                                    <p className="text-[11px] text-slate-500">
                                      {amounts.discountAmount > 0 ? `-${formatMoney(amounts.discountAmount)}` : ''}
                                      {amounts.discountAmount > 0 && amounts.gstAmount > 0 ? ' / ' : ''}
                                      {amounts.gstAmount > 0 ? `+${formatMoney(amounts.gstAmount)} GST` : ''}
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="typeOfMedicine" type="text" placeholder="Type" value={item?.typeOfMedicine || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput name="packing" type="text" placeholder="e.g. 10×10 tabs" value={item?.packing || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="mt-1 w-full">
                              <input name="batchNo" type="text" maxLength={50} placeholder="Batch" value={item?.batchNo || ''} onChange={(e) => handleInputChange(e, index)} className={smallInputClass} />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="mt-1 w-full">
                              <input name="hsnNo" type="text" inputMode="numeric" maxLength={8} placeholder="e.g. 3004" value={item?.hsnNo || ''} onChange={(e) => handleInputChange(e, index)} className={smallInputClass} />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <CustomSelect onChange={(e) => handleInputChange(e, index)} options={distributorsOptions?.map((e) => e?.companyName)} value={item?.distributor || ''} name="distributor" placeholder="-- Select Distributor --" />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput name="expiryDate" type="date" value={item?.expiryDate || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              aria-label="Remove item"
                              disabled={formData.length === 1}
                              onClick={() => removeRow(index)}
                              className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-start justify-between gap-4 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => [...prev, { ...initialFormData }])}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-sky-400 hover:bg-sky-50 hover:text-sky-700"
                >
                  <FiPlus className="h-4 w-4" />
                  Add Another Item
                </button>
                {totals.priced > 0 && (
                  <dl className="w-full max-w-xs space-y-1 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <dt>Subtotal</dt>
                      <dd>{formatMoney(totals.subtotal)}</dd>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <dt>Discount</dt>
                      <dd>{totals.discount > 0 ? `-${formatMoney(totals.discount)}` : formatMoney(0)}</dd>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <dt>GST</dt>
                      <dd>+{formatMoney(totals.gst)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900">
                      <dt>Net Total</dt>
                      <dd>{formatMoney(totals.net)}</dd>
                    </div>
                    {totals.priced < rows.length && (
                      <p className="text-[11px] text-slate-500">
                        {rows.length - totals.priced} line{rows.length - totals.priced === 1 ? '' : 's'} without a rate not included.
                      </p>
                    )}
                  </dl>
                )}
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pb-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="rounded-xl bg-sky-600 px-6 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-600/30 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isProcessing ? 'Please wait...' : isNew ? 'Submit Order & Download PDF' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <CustomSpinner />
        )}
      </div>

      <ToastContainer />
    </div>
  );
}
